import { useState, useCallback } from 'react';
import type { Company } from '../../services/rtkApi';
import {
    useLazyGetBrregCompanyByOrgnrQuery,
    useLazyGetBrregCompanyQuery,
    useLazySearchForCompanyQuery,
} from '../../services/rtkApi';
import { handleOrgnummerValidering } from '../../utils';
import { useFormContext } from '@klp/formgenerator';

export const useCompanySearch = () => {
    // State to store search results
    const [companyOptions, setCompanyOptions] = useState<Array<Company>>([]);

    // Get the current region context from the form context
    const { regionContext } = useFormContext();

    // API hooks for fetching company data
    const [
        searchForBrregCompanies,
        { isFetching: searchForBrregCompaniesIsFetching },
    ] = useLazyGetBrregCompanyQuery();
    const [
        getBrregCompanyByOrgNr,
        { isFetching: getBrregCompanyByOrgNrIsFetching },
    ] = useLazyGetBrregCompanyByOrgnrQuery();
    const [
        searchForRegionCompanies,
        { isFetching: searchForRegionCompaniesIsFetching },
    ] = useLazySearchForCompanyQuery();

    // Searches for companies by name or organization number
    const searchForCompanies = useCallback(
        (val: string) => {
            if (
                regionContext === 'Danmark' ||
                regionContext === 'Sverige' ||
                regionContext === 'Europa'
            ) {
                searchForRegionCompanies({
                    searchValue: val,
                    region: regionContext,
                })
                    .unwrap()
                    .then((r) => setCompanyOptions(r));
            } else {
                // Normalize and validate organization number
                const formattedOrgNr = val.replace(/\s+/g, '').trim();
                const isOrgNumber = handleOrgnummerValidering(
                    formattedOrgNr,
                    'Oslo',
                );

                // Fetch companies from Brreg based on input type
                searchForBrregCompanies({
                    navn: !isOrgNumber ? val : undefined,
                    organisasjonsnummer: isOrgNumber
                        ? formattedOrgNr
                        : undefined,
                })
                    .unwrap()
                    .then((r) =>
                        setCompanyOptions(r?._embedded?.enheter || []),
                    );
            }
        },
        [regionContext, searchForBrregCompanies, searchForRegionCompanies],
    );

    // Fetches a company by its organization number
    const getCompany = useCallback(
        (organisasjonsnummer: Company['organisasjonsnummer']) => {
            if (
                regionContext === 'Danmark' ||
                regionContext === 'Sverige' ||
                regionContext === 'Europa'
            ) {
                searchForRegionCompanies({
                    searchValue: organisasjonsnummer,
                    region: regionContext,
                })
                    .unwrap()
                    .then((r) => setCompanyOptions(r));
            } else if (handleOrgnummerValidering(organisasjonsnummer, 'Oslo')) {
                // Fetch company details for Norway using Brreg
                getBrregCompanyByOrgNr({ organisasjonsnummer })
                    .unwrap()
                    .then((r) => setCompanyOptions([r]));
            }
        },
        [regionContext, searchForRegionCompanies, getBrregCompanyByOrgNr],
    );

    return {
        // List of fetched companies
        companyOptions,

        // Loading state for API requests
        companiesIsLoading:
            searchForBrregCompaniesIsFetching ||
            getBrregCompanyByOrgNrIsFetching ||
            searchForRegionCompaniesIsFetching,

        // Methods for searching companies
        searchForCompanies,
        getCompany,
    };
};
